import { useRef } from 'react';

/**
 * Returns a ref whose `current` is an array of stable string keys aligned with `length`.
 *
 * Each key is generated once and persists across renders for as long as its row exists, so
 * React reconciliation and child component state survive reorders, additions, and removals.
 * The ref is kept in sync with `length` during render — refs are not state, so this does not
 * violate the "no side effects in render" rule and avoids an extra render pass that a
 * `useEffect`-based sync would introduce.
 *
 * Callers that explicitly add or remove a row should mutate `keysRef.current` themselves
 * (insert at index / filter by index) so the new key array still maps 1:1 to the data array.
 * This hook is the safety net for *external* length changes (initial load, toggle seeds, etc.)
 * where the explicit add/remove path is not taken.
 *
 * @param {number} length Current logical row count to align keys with.
 * @param {string} [prefix=row] Prefix for generated keys.
 * @returns {Object} `{ keysRef, makeKey }` — `keysRef.current` is the synced array of stable
 *     keys; `makeKey()` produces one new key (call after an explicit row insertion).
 */
export default function useStableRowKeys(length, prefix = 'row') {
    const nextIdRef = useRef(0);
    const keysRef = useRef([]);
    if (keysRef.current.length !== length) {
        const aligned = keysRef.current.slice(0, length);
        while (aligned.length < length) {
            nextIdRef.current += 1;
            aligned.push(`${prefix}-${nextIdRef.current}`);
        }
        keysRef.current = aligned;
    }
    const makeKey = () => {
        nextIdRef.current += 1;
        return `${prefix}-${nextIdRef.current}`;
    };
    return { keysRef, makeKey };
}
